import { useState } from "react";

import { addDays, isTomorrow, differenceInDays, getDay } from "date-fns";
import { BellIcon, CalendarIcon, Check, Clock, Globe, X } from "lucide-react";
import { toast } from "sonner";

import TimezoneField from "~/components/timezone-field";
import { useApiClient } from "~/hooks/use-api-client";
import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import { Dialog, DialogContent, DialogTrigger } from "~/components/ui/dialog";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";

// AIDEV-NOTE: Schedule a Reminder modal — visual design from claude-design handoff
// (parts-companion/project/components/schedule-reminder-modal.jsx). All fields,
// behaviors, validation, localStorage persistence, and API call match the prior
// dialog. Only the visual layer was restyled to match the Parts Companion design
// system: cream-100 surface, forest-200 borders, terra-600 CTA, pill suggestions.

type ReminderEntityType = "session" | "part" | "commitment";

export interface GenericReminderDialogProps {
    // AIDEV-NOTE: "neutral" is the design system's SECONDARY CTA (white surface, forest-200
    // border, forest-800 text) — the right pairing for a reminder sitting beside a primary
    // solidForest action, and it means callers never reach for className overrides.
    buttonVariant?: "default" | "outline" | "ghost" | "neutral";
    buttonSize?: "default" | "sm" | "lg" | "icon";
    buttonClassName?: string;
    entityType: ReminderEntityType;
    entityId: string;
    entityName: string;
    description?: string;
    analyticsId?: string;
    triggerLabel?: React.ReactNode;
    /** Trigger glyph; commitments use the calendar so the button reads as "schedule". */
    triggerIcon?: "bell" | "calendar";
    /** Accessible name for an icon-only trigger (pass `triggerLabel={null}`). */
    triggerAriaLabel?: string;
    onSaved?: () => void;
    /**
     * Controlled open state. Supply with `hideTrigger` to drive the dialog from
     * somewhere the built-in button can't live — e.g. a dropdown menu item,
     * which can't wrap a DialogTrigger without the menu swallowing the click.
     */
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    /** Render only the dialog, no trigger button. */
    hideTrigger?: boolean;
}

function SectionLabel({
    icon: Icon,
    text,
}: {
    icon: typeof CalendarIcon;
    text: string;
}) {
    return (
        <div className="inline-flex items-center gap-2 text-brand-forest-800">
            <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-lg bg-brand-forest-100 text-brand-forest-700">
                <Icon className="size-[15px]" strokeWidth={1.8} />
            </span>
            <span className="font-body text-[13px] font-semibold tracking-[-0.05px] text-brand-forest-800">
                {text}
            </span>
        </div>
    );
}

function TimeInput({
    value,
    onChange,
    max,
    placeholder,
}: {
    value: string;
    onChange: (v: string) => void;
    max: number;
    placeholder: string;
}) {
    return (
        <input
            type="text"
            inputMode="numeric"
            maxLength={2}
            value={value}
            placeholder={placeholder}
            onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 2);
                if (v === "" || parseInt(v) <= max) {
                    onChange(v);
                }
            }}
            onBlur={(e) => {
                if (e.target.value !== "") {
                    onChange(e.target.value.padStart(2, "0"));
                }
            }}
            className="h-14 w-[84px] rounded-xl border border-brand-forest-200 bg-white p-0 text-center font-display text-2xl font-semibold tracking-[-0.5px] text-brand-forest-800 shadow-[0_1px_2px_rgba(27,67,50,0.04)] outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-800/20"
        />
    );
}

function CustomCheckbox({
    checked,
    onChange,
}: {
    checked: boolean;
    onChange: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onChange}
            aria-checked={checked}
            role="checkbox"
            className={cn(
                "mt-px inline-flex size-5 shrink-0 items-center justify-center rounded-md transition-colors",
                checked
                    ? "border-[1.5px] border-brand-forest-800 bg-brand-forest-800"
                    : "border-[1.5px] border-brand-forest-300 bg-white",
            )}
        >
            {checked && <Check className="size-3 text-white" strokeWidth={3} />}
        </button>
    );
}

export const GenericReminderDialog: React.FC<GenericReminderDialogProps> = ({
    buttonVariant = "default",
    buttonSize = "default",
    buttonClassName,
    entityType,
    entityId,
    entityName,
    description,
    analyticsId = "Schedule reminder",
    triggerLabel = "Remind",
    triggerIcon = "bell",
    triggerAriaLabel,
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
    hideTrigger = false,
    onSaved,
}) => {
    const apiClient = useApiClient();
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    // AIDEV-NOTE: Controlled when `open` is supplied, so a dropdown item can own
    // the state; falls back to internal state for every existing call site.
    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : uncontrolledOpen;
    const setOpen = (next: boolean) => {
        if (!isControlled) setUncontrolledOpen(next);
        controlledOnOpenChange?.(next);
    };
    const [tab, setTab] = useState<"suggestions" | "calendar">("suggestions");
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(
        undefined,
    );
    const [selectedTime, setSelectedTime] = useState({
        hours: "09",
        minutes: "00",
    });
    const [selectedTimezone, setSelectedTimezone] = useState(
        Intl.DateTimeFormat().resolvedOptions().timeZone,
    );
    const [note, setNote] = useState("");
    const [privacyConsent, setPrivacyConsent] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const getRelativeDateLabel = (date: Date) => {
        const today = new Date();
        const dayNames = [
            "Sunday",
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
        ];
        const dayName = dayNames[getDay(date)];

        if (isTomorrow(date)) {
            return "Tomorrow";
        }

        const todayDayOfWeek = getDay(today);
        const daysUntilTarget = differenceInDays(date, today);

        if (todayDayOfWeek >= 1) {
            const daysUntilNextMonday =
                todayDayOfWeek === 0 ? 1 : 8 - todayDayOfWeek;
            if (daysUntilTarget >= daysUntilNextMonday) {
                return `Next ${dayName}`;
            }
        }

        return `This ${dayName}`;
    };

    const getDaysHint = (days: number) =>
        days === 1 ? "in a day" : `in ${days} days`;

    const isTimeInPast = () => {
        if (!selectedDate) return false;

        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
        const day = String(selectedDate.getDate()).padStart(2, "0");
        const hours = (selectedTime.hours || "00").padStart(2, "0");
        const minutes = (selectedTime.minutes || "00").padStart(2, "0");

        const selectedDateTime = new Date(
            `${year}-${month}-${day}T${hours}:${minutes}:00`,
        );
        const now = new Date();

        return selectedDateTime <= now;
    };

    const handleSave = async () => {
        if (!selectedDate) {
            toast.error("Please select a date");
            return;
        }

        if (!privacyConsent) {
            toast.error("Please accept the privacy release");
            return;
        }

        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
        const day = String(selectedDate.getDate()).padStart(2, "0");
        const hours = (selectedTime.hours || "00").padStart(2, "0");
        const minutes = (selectedTime.minutes || "00").padStart(2, "0");

        const tempDate = new Date(
            `${year}-${month}-${day}T${hours}:${minutes}:00`,
        );

        const now = new Date();
        if (tempDate <= now) {
            toast.error("Please select a time in the future");
            return;
        }

        // AIDEV-NOTE: tempDate is already in user's local timezone — store as UTC.
        const utcReminderDateTime = tempDate;

        const existing = JSON.parse(localStorage.getItem("reminders") || "[]");
        existing.push({
            type: entityType,
            id: entityId,
            name: entityName,
            reminderDate: utcReminderDateTime.toISOString(),
            timezone: selectedTimezone,
            note: note.trim(),
            createdAt: new Date().toISOString(),
        });
        localStorage.setItem("reminders", JSON.stringify(existing));

        setIsSubmitting(true);
        try {
            const response = await apiClient.post("/reminders/schedule", {
                entityType,
                entityId,
                entityName,
                reminderDate: utcReminderDateTime.toISOString(),
                timezone: selectedTimezone,
                note: note.trim(),
            });

            const data = (await response.json()) as {
                success?: boolean;
                error?: string;
            };

            if (response.ok && data.success) {
                toast.success("Reminder scheduled successfully");
                try {
                    window.dispatchEvent(new Event("reminders-updated"));
                } catch {
                    // no-op
                }
                onSaved?.();
                setOpen(false);

                // Reset form
                setSelectedDate(undefined);
                setSelectedTime({ hours: "09", minutes: "00" });
                setSelectedTimezone(
                    Intl.DateTimeFormat().resolvedOptions().timeZone,
                );
                setNote("");
                setPrivacyConsent(false);
            } else if (data.error) {
                toast.error(data.error);
            }
        } catch {
            toast.error("Failed to schedule reminder");
        } finally {
            setIsSubmitting(false);
        }
    };

    const defaultDescription =
        entityType === "session"
            ? "Schedule a reminder to come back and integrate this session."
            : entityType === "part"
              ? "Schedule a reminder to check in with this part."
              : "Schedule a reminder to follow up on this commitment.";

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);

    const showTimeError = isTimeInPast() && !!selectedDate;

    // AIDEV-NOTE: Derive 12-hour display + AM/PM from 24h storage so the rest of
    // the component (save/validation) keeps working on 24h hours.
    const hours24 = parseInt(selectedTime.hours || "0", 10);
    const period: "AM" | "PM" = hours24 >= 12 ? "PM" : "AM";
    const display12HoursNum = hours24 % 12 === 0 ? 12 : hours24 % 12;
    const display12Hours = selectedTime.hours
        ? String(display12HoursNum).padStart(2, "0")
        : "";
    const to24Hour = (h12: number, p: "AM" | "PM"): number => {
        const clamped = Math.min(12, Math.max(1, h12));
        if (p === "AM") return clamped === 12 ? 0 : clamped;
        return clamped === 12 ? 12 : clamped + 12;
    };

    // Build the 7 suggestion entries plus a leading "Today" entry (in 2 hours).
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayIsSelected =
        selectedDate?.getDate() === today.getDate() &&
        selectedDate?.getMonth() === today.getMonth() &&
        selectedDate?.getFullYear() === today.getFullYear();

    return (
        <Dialog open={open} onOpenChange={setOpen} analyticsId={analyticsId}>
            {!hideTrigger && (
                <DialogTrigger asChild>
                    <Button
                        variant={buttonVariant || "default"}
                        size={buttonSize}
                        aria-label={triggerAriaLabel}
                        className={cn(
                            "flex items-center gap-2 text-sm",
                            buttonClassName,
                        )}
                    >
                        {triggerIcon === "calendar" ? (
                            <CalendarIcon className="size-4" />
                        ) : (
                            <BellIcon className="size-4" />
                        )}
                        {triggerLabel}
                    </Button>
                </DialogTrigger>
            )}
            {/* AIDEV-NOTE: showCloseButton=false so we can render a custom forest-bordered close
                button inside the header. The fixed inset styles in DialogContent are overridden
                by our cream surface, large radius, and forest-tinted shadow. */}
            <DialogContent
                showCloseButton={false}
                className="w-[min(880px,calc(100vw-48px))] max-w-[880px] gap-0 overflow-hidden rounded-[20px] rounded-tl-[20px] rounded-br-[20px] border border-brand-forest-200 bg-brand-cream-100 p-0 shadow-[0_32px_80px_-20px_rgba(27,67,50,0.32),0_12px_24px_-12px_rgba(27,67,50,0.18)] max-h-[calc(100vh-48px)]"
            >
                <div className="flex max-h-[calc(100vh-48px)] flex-col">
                    {/* Header */}
                    <div className="flex items-start gap-4 border-b border-brand-forest-100 px-8 pb-[18px] pt-6">
                        <div className="min-w-0 flex-1">
                            <h2 className="m-0 font-display text-[26px] font-bold leading-[1.15] tracking-[-0.3px] text-brand-forest-800">
                                Schedule a Reminder
                            </h2>
                            <p className="mt-1.5 max-w-[560px] font-body text-sm leading-[1.55] text-brand-forest-700">
                                {description || defaultDescription}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            aria-label="Close"
                            className="inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-brand-forest-200 bg-transparent text-brand-forest-700 transition-colors hover:bg-brand-forest-50"
                        >
                            <X className="size-3.5" strokeWidth={1.8} />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="grid min-h-0 flex-1 grid-cols-1 gap-9 overflow-y-auto px-8 pb-2 pt-6 md:grid-cols-2">
                        {/* LEFT: date */}
                        <div className="flex min-w-0 flex-col gap-3.5">
                            <SectionLabel
                                icon={CalendarIcon}
                                text="Select Date"
                            />

                            {/* Segmented tabs */}
                            <div className="inline-flex gap-1 self-stretch rounded-xl bg-brand-forest-100 p-1">
                                {(
                                    [
                                        {
                                            id: "suggestions",
                                            label: "Suggestions",
                                        },
                                        { id: "calendar", label: "Calendar" },
                                    ] as const
                                ).map((t) => {
                                    const on = tab === t.id;
                                    return (
                                        <button
                                            key={t.id}
                                            type="button"
                                            onClick={() => setTab(t.id)}
                                            className={cn(
                                                "flex-1 rounded-[9px] font-body text-[13px] transition-colors",
                                                "h-[34px]",
                                                on
                                                    ? "bg-white font-semibold text-brand-forest-800 shadow-[0_1px_2px_rgba(27,67,50,0.08)]"
                                                    : "bg-transparent font-medium text-brand-forest-700 hover:text-brand-forest-800",
                                            )}
                                        >
                                            {t.label}
                                        </button>
                                    );
                                })}
                            </div>

                            {tab === "suggestions" ? (
                                <div className="flex flex-col gap-2">
                                    {/* Today (sets time to 2 hours from now) */}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSelectedDate(today);
                                            const twoHoursFromNow = new Date();
                                            twoHoursFromNow.setHours(
                                                twoHoursFromNow.getHours() + 2,
                                            );
                                            setSelectedTime({
                                                hours: String(
                                                    twoHoursFromNow.getHours(),
                                                ).padStart(2, "0"),
                                                minutes: String(
                                                    twoHoursFromNow.getMinutes(),
                                                ).padStart(2, "0"),
                                            });
                                        }}
                                        className={cn(
                                            "flex items-center justify-between gap-3 rounded-[20px] border px-4 py-2.5 text-left transition-all",
                                            "font-body text-sm font-semibold",
                                            todayIsSelected
                                                ? "border-brand-terra-600 bg-brand-terra-600 text-white shadow-[0_2px_6px_-2px_rgba(216,90,60,0.5)]"
                                                : "border-brand-forest-200 bg-white text-brand-terra-600 hover:border-brand-forest-300 hover:bg-brand-forest-50",
                                        )}
                                    >
                                        <span>Today</span>
                                        <span
                                            className={cn(
                                                "font-body text-xs font-medium",
                                                todayIsSelected
                                                    ? "text-white/85"
                                                    : "text-brand-forest-500",
                                            )}
                                        >
                                            in 2 hours
                                        </span>
                                    </button>
                                    {[1, 2, 3, 4, 5, 6, 7].map((days) => {
                                        const targetDate = addDays(
                                            new Date(),
                                            days,
                                        );
                                        targetDate.setHours(0, 0, 0, 0);

                                        const isSelected =
                                            selectedDate?.getDate() ===
                                                targetDate.getDate() &&
                                            selectedDate?.getMonth() ===
                                                targetDate.getMonth() &&
                                            selectedDate?.getFullYear() ===
                                                targetDate.getFullYear();

                                        return (
                                            <button
                                                key={days}
                                                type="button"
                                                onClick={() =>
                                                    setSelectedDate(targetDate)
                                                }
                                                className={cn(
                                                    "flex items-center justify-between gap-3 rounded-[20px] border px-4 py-2.5 text-left transition-all",
                                                    "font-body text-sm font-semibold",
                                                    isSelected
                                                        ? "border-brand-terra-600 bg-brand-terra-600 text-white shadow-[0_2px_6px_-2px_rgba(216,90,60,0.5)]"
                                                        : "border-brand-forest-200 bg-white text-brand-terra-600 hover:border-brand-forest-300 hover:bg-brand-forest-50",
                                                )}
                                            >
                                                <span>
                                                    {getRelativeDateLabel(
                                                        targetDate,
                                                    )}
                                                </span>
                                                <span
                                                    className={cn(
                                                        "font-body text-xs font-medium",
                                                        isSelected
                                                            ? "text-white/85"
                                                            : "text-brand-forest-500",
                                                    )}
                                                >
                                                    {getDaysHint(days)}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="rounded-[14px] border border-brand-forest-200 bg-white p-4 shadow-[0_1px_2px_rgba(27,67,50,0.04)]">
                                    <Calendar
                                        mode="single"
                                        selected={selectedDate}
                                        onSelect={setSelectedDate}
                                        disabled={(date) => date <= yesterday}
                                    />
                                </div>
                            )}
                        </div>

                        {/* RIGHT: time / tz / note */}
                        <div className="flex min-w-0 flex-col gap-6">
                            {/* Time */}
                            <div className="flex flex-col gap-3">
                                <SectionLabel icon={Clock} text="Select Time" />
                                <div className="flex items-center gap-2.5">
                                    <TimeInput
                                        // AIDEV-NOTE: Display in 12h, but selectedTime.hours stays 24h.
                                        // Convert on input/blur so save/validation logic doesn't change.
                                        value={display12Hours}
                                        onChange={(v) => {
                                            if (v === "") {
                                                setSelectedTime({
                                                    ...selectedTime,
                                                    hours: "",
                                                });
                                                return;
                                            }
                                            const n = parseInt(v, 10);
                                            if (n < 0 || n > 12) return;
                                            // Clamp 0 → 12 since 12h clock has no "0".
                                            const h12 = n === 0 ? 12 : n;
                                            const h24 = to24Hour(h12, period);
                                            setSelectedTime({
                                                ...selectedTime,
                                                hours: String(h24).padStart(
                                                    2,
                                                    "0",
                                                ),
                                            });
                                        }}
                                        max={12}
                                        placeholder="HH"
                                    />
                                    <span className="font-display text-2xl font-semibold leading-none text-brand-forest-500">
                                        :
                                    </span>
                                    <TimeInput
                                        value={selectedTime.minutes}
                                        onChange={(v) =>
                                            setSelectedTime({
                                                ...selectedTime,
                                                minutes: v,
                                            })
                                        }
                                        max={59}
                                        placeholder="MM"
                                    />
                                    <div className="ml-auto inline-flex items-center gap-[3px] rounded-[10px] border border-brand-forest-200 bg-white p-[3px]">
                                        {(["AM", "PM"] as const).map((p) => {
                                            const on = period === p;
                                            return (
                                                <button
                                                    key={p}
                                                    type="button"
                                                    onClick={() => {
                                                        if (period === p)
                                                            return;
                                                        const current12 =
                                                            parseInt(
                                                                display12Hours ||
                                                                    "12",
                                                                10,
                                                            );
                                                        const h24 = to24Hour(
                                                            current12,
                                                            p,
                                                        );
                                                        setSelectedTime({
                                                            ...selectedTime,
                                                            hours: String(
                                                                h24,
                                                            ).padStart(2, "0"),
                                                        });
                                                    }}
                                                    aria-pressed={on}
                                                    className={cn(
                                                        "inline-flex h-7 items-center rounded-[7px] px-3 font-body text-xs font-semibold transition-colors",
                                                        on
                                                            ? "bg-brand-forest-800 text-white"
                                                            : "bg-transparent text-brand-forest-500 hover:text-brand-forest-700",
                                                    )}
                                                >
                                                    {p}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Timezone */}
                            <div className="flex flex-col gap-3">
                                <SectionLabel icon={Globe} text="Timezone" />
                                <TimezoneField
                                    value={selectedTimezone}
                                    onChange={setSelectedTimezone}
                                />
                            </div>

                            {/* Note */}
                            <div className="flex flex-col gap-2.5">
                                <label
                                    htmlFor="reminder-note"
                                    className="font-body text-[13px] font-semibold tracking-[-0.05px] text-brand-forest-800"
                                >
                                    Note{" "}
                                    <span className="font-normal text-brand-forest-500">
                                        (optional)
                                    </span>
                                </label>
                                <textarea
                                    id="reminder-note"
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    rows={4}
                                    placeholder="Add a personal note for your reminder…"
                                    className="min-h-24 w-full resize-y rounded-[10px] border border-brand-forest-200 bg-white px-3.5 py-3 font-body text-sm leading-[1.55] text-brand-forest-800 shadow-[0_1px_2px_rgba(27,67,50,0.04)] outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-800/20"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Time validation error */}
                    {showTimeError && (
                        <div className="px-8 pt-3">
                            <div className="rounded-[10px] border border-[#f5bdab] bg-[#fdf4f1] px-4 py-2.5 font-body text-[13px] text-[#9a2a1f]">
                                The selected time is in the past. Please select
                                a future time.
                            </div>
                        </div>
                    )}

                    {/* Privacy release */}
                    <div className="px-8 pb-1 pt-3">
                        <label
                            className="flex cursor-pointer items-start gap-3 rounded-xl border border-brand-forest-100 bg-brand-forest-50 px-4 py-3.5"
                            onClick={(e) => {
                                // Prevent double-toggle when clicking the inner button.
                                if (
                                    (e.target as HTMLElement).closest("button")
                                ) {
                                    return;
                                }
                                setPrivacyConsent((p) => !p);
                            }}
                        >
                            <CustomCheckbox
                                checked={privacyConsent}
                                onChange={() => setPrivacyConsent((p) => !p)}
                            />
                            <span className="flex-1 font-body text-[13px] leading-[1.55] text-brand-forest-700">
                                <strong className="font-semibold text-brand-forest-800">
                                    Privacy release:
                                </strong>{" "}
                                I authorize Parts Companion to include info
                                about my parts/session in the email it sends.
                            </span>
                        </label>
                    </div>

                    {/* Footer — mobile-friendly: full-width buttons stacked on small screens. */}
                    <div className="flex flex-col items-stretch gap-2.5 px-8 pb-[22px] pt-4 sm:flex-row sm:items-center sm:justify-end">
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="inline-flex h-[42px] w-full items-center justify-center rounded-[20px] border border-brand-forest-200 bg-transparent px-5 font-body text-sm font-semibold text-brand-forest-800 transition-colors hover:bg-brand-forest-50 sm:w-auto"
                        >
                            Cancel
                        </button>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span
                                        tabIndex={0}
                                        className="block w-full sm:w-auto"
                                    >
                                        <button
                                            type="button"
                                            onClick={handleSave}
                                            disabled={
                                                !selectedDate ||
                                                !privacyConsent ||
                                                isSubmitting
                                            }
                                            className={cn(
                                                "inline-flex h-[42px] w-full items-center justify-center gap-2 rounded-[20px] border-0 bg-brand-terra-600 px-[22px] font-body text-sm font-semibold text-white transition-all sm:w-auto",
                                                "shadow-[0_6px_14px_-6px_rgba(216,90,60,0.55),0_2px_4px_rgba(216,90,60,0.18)]",
                                                "hover:bg-brand-terra-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none",
                                            )}
                                        >
                                            <CalendarIcon
                                                className="size-[15px]"
                                                strokeWidth={1.8}
                                            />
                                            {isSubmitting
                                                ? "Saving…"
                                                : "Save Reminder"}
                                        </button>
                                    </span>
                                </TooltipTrigger>
                                {(!selectedDate || !privacyConsent) && (
                                    <TooltipContent>
                                        <p>
                                            {!selectedDate
                                                ? "Please select a date"
                                                : !privacyConsent
                                                  ? "Accept privacy release"
                                                  : ""}
                                        </p>
                                    </TooltipContent>
                                )}
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default GenericReminderDialog;
